import React, { useState } from 'react';
import PropTypes, { any } from 'prop-types';

import Filter, { FilterModal, FilterSection } from '../../../SearchPage/Filters/Filter';

import styles from '../../../SearchPage/search.scss';
import { divisionFormatTypeIds, gameTypeValues } from '../../../../utils/eventConstants';
import DebouncedFilterInput from '../../../../components/FormElementsStandalone/DebouncedFilterInput';
import SingleCheckbox from '../../../SearchPage/Filters/FilterComponents/CoreComponents/SingleCheckbox';
import Collapsible from '../../../../components/Form/Collapsible';
import DropdownCheckBoxFilter from './DropdownCheckBoxFilter';
import { sortObjectsByNameAsc } from '../../../../utils/sorting';

const DivisionFilter = (props) => {
    const { filters, filterModal, handleFilterClick, closeModals, filterInputs, handleFilterSubmit, handleFilterUpdate, mobileOnly, desktopOnly, filteredDivisions = [], 'data-testid': dataTestId } = props;

    const [filter, setFilter] = useState('');
    const [isSinglesOpen, setIsSinglesOpen] = useState(true);
    const [isDoublesOpen, setIsDoublesOpen] = useState(true);

    const singlesIsSelected = filterInputs?.playerType === gameTypeValues.SINGLES;
    const doublesIsSelected = filterInputs?.playerType === gameTypeValues.DOUBLES;

    const filterString = filter.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
    const filterRegex = new RegExp(filterString, 'i');

    const singlesDivisions = filteredDivisions
        .filter(div => {
            if (filter && !div.name?.match(filterRegex)) {
                return false;
            }
            return div.divisionFormatTypeId === divisionFormatTypeIds.SINGLES;
        })
        .sort(sortObjectsByNameAsc)
        .map(div => ({ label: div.name, value: div.id, count: div.registeredCount }));

    const doublesDivisions = filteredDivisions
        .filter((div) => {
            if (filter && !div.name?.match(filterRegex)) {
                return false;
            }
            return div.divisionFormatTypeId !== divisionFormatTypeIds.SINGLES;
        })
        .sort(sortObjectsByNameAsc)
        .map(div => ({ label: div.name, value: div.id, count: div.registeredCount }));

    const addOrRemoveId = (id) => {
        let updated;
        if (filterInputs.selectedDivs.includes(id)) {
            updated = filterInputs.selectedDivs.filter(item => item !== id);
        } else {
            updated = [...filterInputs.selectedDivs, id];
        }
        return updated;
    };

    const updateFilter = (value) => {
        setFilter(value);
    };

    const handleAllSinglesClick = () => {
        let updated;
        if (filterInputs.selectedDivs.length === singlesDivisions.length && (!filterInputs.playerType || filterInputs.playerType === gameTypeValues.SINGLES)) {
            updated = [];
        } else {
            updated = singlesDivisions.map(div => div.value);
        }
        handleFilterUpdate({
            selectedDivs: updated,
            playerType: updated.length ? gameTypeValues.SINGLES : '',
        });
    };

    const isSinglesAllSelected = filterInputs.selectedDivs.length === singlesDivisions.length;

    const handleAllDoublesClick = () => {
        let updated;
        if (filterInputs.selectedDivs.length === doublesDivisions.length && (!filterInputs.playerType || filterInputs.playerType === gameTypeValues.DOUBLES)) {
            updated = [];
        } else {
            updated = doublesDivisions.map(div => div.value);
        }
        handleFilterUpdate({
            selectedDivs: updated,
            playerType: updated.length ? gameTypeValues.DOUBLES : '',
        });
    };

    const isDoublesAllSelected = filterInputs.selectedDivs.length === doublesDivisions.length;

    const handleCollapseSingles = () => {
        setIsSinglesOpen(!isSinglesOpen);
    };

    const handleCollapseDoubles = () => {
        setIsDoublesOpen(!isDoublesOpen);
    };

    const filterName = 'selectedDivs';

    return (filteredDivisions.length) ? (
        <div className={styles.filterWrapper}>
            <Filter
                filterName={filterName}
                handleFilterClick={handleFilterClick}
                isActive={!!filters?.selectedDivs?.length || filterModal === filterName}
                mobileOnly={mobileOnly}
                desktopOnly={desktopOnly}
                data-testid={dataTestId}
            >
                <div className={styles.flexContainer}>
                    <span className={styles.textStyle} data-testid={dataTestId && `${dataTestId}.label`}>
                        All divisions
                    </span>
                    <i className={`uts uts-expand ${styles.iconStyle}`} />
                </div>
            </Filter>
            {filterModal === filterName && (
                <FilterModal
                    title={'Selected Divisions'}
                    handleFilterSubmit={handleFilterSubmit}
                    onCloseClick={closeModals}
                    data-testid={dataTestId}
                >
                    <FilterSection
                        style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                        }}
                    >
                        <DebouncedFilterInput
                            value={filter}
                            onChange={updateFilter}
                            placeholder="Filter Divisions"
                            data-testid={dataTestId && `${dataTestId}.search-input`}
                        />
                        {!doublesIsSelected && !!singlesDivisions.length && (
                            <>
                                <Collapsible
                                    isOpen={isSinglesOpen}
                                    onClick={handleCollapseSingles}
                                    data-testid={dataTestId && `${dataTestId}.singles-collapsible`}
                                >
                                    <SingleCheckbox
                                        id="singles"
                                        label={`SINGLES (${singlesDivisions.length})`}
                                        onChange={handleAllSinglesClick}
                                        isChecked={isSinglesAllSelected}
                                        data-testid={dataTestId && `${dataTestId}.singles-select-all`}
                                    />
                                </Collapsible>
                                {isSinglesOpen && (
                                    <div className="ml24">
                                        <DropdownCheckBoxFilter
                                            options={singlesDivisions}
                                            filterInputs={filterInputs}
                                            filterInputName={filterName}
                                            onChange={(value) => {
                                                const updatedDivs = addOrRemoveId(value);
                                                handleFilterUpdate({
                                                    selectedDivs: updatedDivs,
                                                    playerType: updatedDivs.length ? gameTypeValues.SINGLES : '',
                                                });
                                            }}
                                            isArray
                                            isDivision={true}
                                            data-testid={dataTestId && `${dataTestId}.singles-options`}
                                        />
                                    </div>

                                )}
                            </>
                        )}
                        {!singlesIsSelected && !!doublesDivisions.length && (
                            <>
                                <Collapsible
                                    isOpen={isDoublesOpen}
                                    onClick={handleCollapseDoubles}
                                    data-testid={dataTestId && `${dataTestId}.doubles-collapsible`}
                                >
                                    <SingleCheckbox
                                        id="doubles"
                                        label={`DOUBLES (${doublesDivisions.length})`}
                                        onChange={handleAllDoublesClick}
                                        isChecked={isDoublesAllSelected}
                                        data-testid={dataTestId && `${dataTestId}.doubles-select-all`}
                                    />
                                </Collapsible>
                                {isDoublesOpen && (
                                    <div className="ml24">
                                        <DropdownCheckBoxFilter
                                            options={doublesDivisions}
                                            filterInputs={filterInputs}
                                            filterInputName={filterName}
                                            onChange={(value) => {
                                                const updatedDivs = addOrRemoveId(value);
                                                handleFilterUpdate({
                                                    selectedDivs: updatedDivs,
                                                    playerType: updatedDivs.length ? gameTypeValues.DOUBLES : '',
                                                });
                                            }}
                                            isArray
                                            isDivision={true}
                                            data-testid={dataTestId && `${dataTestId}.doubles-options`}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </FilterSection>
                </FilterModal>
            )}
        </div>
    ) : null;
};

DivisionFilter.propTypes = {
    filters: PropTypes.objectOf(any).isRequired,
    filterModal: PropTypes.objectOf(any).isRequired,
    handleFilterClick: PropTypes.func.isRequired,
    closeModals: PropTypes.func.isRequired,
    filterInputs: PropTypes.objectOf(any).isRequired,
    handleFilterUpdate: PropTypes.func.isRequired,
    handleFilterSubmit: PropTypes.func.isRequired,
    mobileOnly: PropTypes.bool,
    desktopOnly: PropTypes.bool,
    filteredDivisions: PropTypes.arrayOf(any),
    'data-testid': PropTypes.string,
};

DivisionFilter.defaultProps = {
    mobileOnly: false,
    desktopOnly: false,
    filteredDivisions: [],
    'data-testid': undefined,
};

export default DivisionFilter;
